import { useQuery } from '@tanstack/react-query-latest';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useContext, type ReactElement } from 'react';
import { DialogTrigger, TooltipTrigger } from 'react-aria-components';
import { fromEntries as rFromEntries, map as rMap, pick as rPick, pipe as rPipe } from 'remeda';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { LOCAL_STORAGE } from '@/constants/storage';
import { OrganizationUserApiKeyStatusEnum } from '@/libs/openapi';
import type { operations } from '@/libs/openapi';
import {
  AddPlusStrokeIcon,
  Dialog,
  EmptyState,
  Heading,
  HelpCircleStrokeIcon,
  IconButton,
  Paragraph,
  type SkeletonRow,
  Text,
  Tooltip,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterMenuItemLink } from '@/libs/tanstack-router';
import { organizationUserApiKeyQueries, type OrganizationUSerApiKeyQueryResultData } from '@/resources/organization-user-api-key/organization-user-api-key.api';
import { toApiOrganizationUserApiKeyFieldAdapter } from '@/resources/organization-user-api-key/organization-user-api-key.field-adapter';
import { organizationUserApiKeyFilterListAdapter } from '@/resources/organization-user-api-key/organization-user-api-key.filter-list-adapter';
import { organizationUserApiKeyPolicies } from '@/resources/organization-user-api-key/organization-user-api-key.policies';
import { organizationUserApiKeyTableOptions } from '@/resources/organization-user-api-key/organization-user-api-key.table';
import type { ListRouteContext } from '@/routes/_main/_list';
import { ActiveFilters } from '@/routes/_main/_list/-components/active-filters.component';
import { FullscreenTable } from '@/routes/_main/_list/-components/fullscreen-table.component';
import { NewRecordContext, NewRecordsProvider } from '@/routes/_main/_list/-components/new-records-provider.component';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Search } from '@/routes/_main/_list/-components/search.component';
import { ApiKeyFiltersForm } from '@/routes/_main/_list/api-keys/-components/api-key-filters-form.component';
import { CreateApiKeyDialogContent } from '@/routes/_main/_list/api-keys/-components/create-api-key-dialog.component';
import { SwitchStatusMenuItem } from '@/routes/_main/_list/api-keys/-components/switch-status-menu-item.component';
import { Resource } from '@/types/resource.type';

const SEARCH_FILTER_ID: keyof Exclude<operations['OrganizationUserApiKeyController_getMany']['parameters']['query'], undefined> = 'name';

export const Route = createFileRoute('/_main/_list/api-keys/')({
  component: RouteComponent,
  beforeLoad: ({ search, context }) => {
    if (context.user && !context.can.apiKeys.view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: Resource.organizationUserApiKey as const,
      can: {
        [Resource.organizationUserApiKey as const]: rPick(organizationUserApiKeyPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update' ]),
      },
      queryOptions: {
        list: organizationUserApiKeyQueries.list({
          paging: search.pagination,
          sorting: search.sorting,
          filter: toApiOrganizationUserApiKeyFieldAdapter.transform(rPipe(
            search.columnFilters,
            rMap(({ id, value }) => [ id, value ] as const),
            rFromEntries(),
          )),
        }),
      },
    } satisfies ListRouteContext;
  },
  loader: ({ context: { queryClient, queryOptions }}) => {
    queryClient.prefetchQuery(queryOptions.list);
  },
  staticData: {
    list: {
      heading: 'API keys',
      slots: {
        providerComponent: NewRecordsProvider,
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: `TrendMD | API keys`,
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const search = Route.useSearch();

  const [ columnsConfig ] = useLocalStorage(LOCAL_STORAGE.columnsConfig(routeContext.resource).key, organizationUserApiKeyTableOptions.initialConfig);

  const query = useQuery(routeContext.queryOptions.list);

  const config = organizationUserApiKeyTableOptions(query.data?.data ?? [], columnsConfig);
  const rows: Array<OrganizationUSerApiKeyQueryResultData | SkeletonRow> = query.isLoading ? config.getSkeletonRows() : query.data?.data ?? [];

  return (
    <FullscreenTable
      ariaLabel="API keys"
      config={config}
      getRowId={(row) => row.original.apiKey}
      getRowVariant={(row, newRecord) => (row.original.apiKey === newRecord ? 'new' : row.original.status === OrganizationUserApiKeyStatusEnum.Value0 ? 'inactive' : undefined)}
      isLoading={query.isLoading}
      renderExtraMenuItems={(row) => <SwitchStatusMenuItem {...row} />}
      rows={rows}
      renderEmptyState={() => (!query.isFetching ?
        <EmptyState>
          <Heading>{search.columnFilters.length ? 'No data available' : 'No API keys yet'}</Heading>
          {!search.columnFilters.length && <Paragraph>Generate an API key to allow programmatic access to your campaigns.</Paragraph>}
        </EmptyState>
        : undefined)}
    />
  );
}

function ToolbarActionsComponent(): ReactElement | false {
  const routeContext = Route.useRouteContext();

  const newRecordContext = useContext(NewRecordContext);

  const isSmallScreen = useMediaQuery(breakpoints.small);

  return !isSmallScreen && (
    <>
      {routeContext.can.organizationUserApiKey.create() &&
          <DialogTrigger>
            <TooltipTrigger>
              <IconButton aria-label="Create API key">
                <AddPlusStrokeIcon />
              </IconButton>
              <Tooltip>Create API key</Tooltip>
            </TooltipTrigger>
            <Dialog>
              {(renderProps) => (
                <CreateApiKeyDialogContent
                  onClose={renderProps.close}
                  onCreated={(newRecordId) => newRecordContext.setNewRecord(newRecordId)}
                />
              )}
            </Dialog>
          </DialogTrigger>}
      <SecondaryActionsIconButton isTableActions>
        <RouterMenuItemLink to="/api-keys/guide">
          <HelpCircleStrokeIcon />
          <Text>API guide</Text>
        </RouterMenuItemLink>
      </SecondaryActionsIconButton>
    </>
  );
}

function ToolbarLeftComponent(): ReactElement {
  const routeContext = Route.useRouteContext();

  const newRecordContext = useContext(NewRecordContext);

  const isSmallScreen = useMediaQuery(breakpoints.small);

  return (
    <>
      <Search resource={routeContext.resource} searchFilterId={SEARCH_FILTER_ID}>
        {(renderProps) => <ApiKeyFiltersForm onClose={renderProps.close} />}
      </Search>
      <ActiveFilters filterListAdapter={organizationUserApiKeyFilterListAdapter} searchFilterId={SEARCH_FILTER_ID} />
      {isSmallScreen &&
        <>
          {routeContext.can.organizationUserApiKey.create() &&
              <DialogTrigger>
                <TooltipTrigger>
                  <IconButton aria-label="Create API key">
                    <AddPlusStrokeIcon />
                  </IconButton>
                  <Tooltip>Create API key</Tooltip>
                </TooltipTrigger>
                <Dialog>
                  {(renderProps) => (
                    <CreateApiKeyDialogContent
                      onClose={renderProps.close}
                      onCreated={(newRecordId) => newRecordContext.setNewRecord(newRecordId)}
                    />
                  )}
                </Dialog>
              </DialogTrigger>}
          <SecondaryActionsIconButton isTableActions>
            <RouterMenuItemLink to="/api-keys/guide">
              <HelpCircleStrokeIcon />
              <Text>API guide</Text>
            </RouterMenuItemLink>
          </SecondaryActionsIconButton>
        </>}
    </>
  );
}
